import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/components/AuthProvider";
import { Settings, CreditCard, LogOut, ChevronDown, Cable, Users, AlertTriangle } from "lucide-react";
import { useState, useEffect } from "react";
import { AccountSheet } from "@/components/dashboard/AccountSheet";
import { SubscriptionDialog } from "@/components/dashboard/SubscriptionDialog";
import { CrmIntegrationDialog } from "@/components/dashboard/CrmIntegrationDialog";
import { TeamMembersDialog } from "@/components/dashboard/TeamMembersDialog";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";

export function UserMenu() {
  const { user, signOut } = useAuth();
  const [showSettings, setShowSettings] = useState(false);
  const [showBilling, setShowBilling] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showTeamMembers, setShowTeamMembers] = useState(false);
  const { data: subscriptionStatus } = useSubscriptionStatus(user?.id);

  useEffect(() => {
    console.log("[UserMenu] Subscription status:", subscriptionStatus);
    console.log("[UserMenu] Plan type:", subscriptionStatus?.plan);
    console.log("[UserMenu] Is custom plan:", subscriptionStatus?.plan === 'custom');
  }, [subscriptionStatus]);

  const getPlanLabel = () => {
    console.log("[UserMenu] Getting plan label for plan:", subscriptionStatus?.plan);
    
    if (!subscriptionStatus) return "Free Plan";
    
    // Show payment issue warning when needed
    if (subscriptionStatus.hasPaymentIssues && subscriptionStatus.status !== 'active') {
      console.log("[UserMenu] Has payment issues, returning 'Payment Issue'");
      return "Payment Issue";
    }
    
    // First check if it's a custom plan (regardless of subscription status)
    if (subscriptionStatus.plan === 'custom') {
      console.log("[UserMenu] Custom plan detected, returning 'Custom Plan'");
      return "Custom Plan";
    }
    
    // If subscription is active, show the plan name
    if (subscriptionStatus.hasActiveSubscription) {
      console.log("[UserMenu] Has active subscription with plan:", subscriptionStatus.plan);
      
      switch (subscriptionStatus.plan) {
        case "business":
          return "Business Plan";
        case "growth":
          return "Growth Plan";
        case "starter":
          return "Starter Plan";
        case "trial":
          return "Trial Plan";
        default:
          // Safely handle plan formatting with type assertion
          const plan = subscriptionStatus.plan;
          if (plan) {
            return `${plan.charAt(0).toUpperCase()}${plan.slice(1)} Plan`;
          }
          return "Free Plan";
      }
    } else if (subscriptionStatus.isInTrialPeriod) {
      console.log("[UserMenu] In trial period, but plan is:", subscriptionStatus.plan);
      // Even if in trial, if plan is custom, show Custom Plan
      if (subscriptionStatus.plan === 'custom') {
        return "Custom Plan (Trial)";
      }
      return "Trial Plan";
    }
    
    console.log("[UserMenu] Defaulting to Free Plan");
    return "Free Plan";
  };

  const handleSignOut = () => {
    console.log("[UserMenu-Sidebar] Initiating sign out");
    signOut();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center w-full gap-2 p-2 rounded-lg hover:bg-gray-100">
            <Avatar className="w-8 h-8">
              <AvatarImage src={user?.user_metadata?.avatar_url} />
              <AvatarFallback>
                {user?.email?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-medium text-gray-700 truncate max-w-full break-words">
                {user?.email}
              </p>
              <p className={`text-xs ${subscriptionStatus?.hasPaymentIssues && subscriptionStatus.status !== 'active' ? 'text-red-500 font-medium flex items-center' : 'text-gray-500'}`}>
                {subscriptionStatus?.hasPaymentIssues && subscriptionStatus.status !== 'active' && <AlertTriangle className="w-3 h-3 mr-1" />}
                {getPlanLabel()}
              </p>
            </div>
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 bg-white" align="end" side="right" forceMount>
          <DropdownMenuGroup>
            <DropdownMenuItem className="hover:bg-gray-100" onClick={() => setShowSettings(true)}>
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem className="hover:bg-gray-100" onClick={() => setShowTeamMembers(true)}>
              <Users className="w-4 h-4 mr-2" />
              Users
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="hover:bg-gray-100" onClick={() => setShowIntegrations(true)}>
              <Cable className="w-4 h-4 mr-2" />
              Integrations
            </DropdownMenuItem>
            {/* Billing button commented out - functionality preserved */}
            {/* <DropdownMenuItem className={`hover:bg-gray-100 ${subscriptionStatus?.hasPaymentIssues && subscriptionStatus.status !== 'active' ? 'text-red-500 font-medium' : ''}`} onClick={() => setShowBilling(true)}>
              <CreditCard className={`w-4 h-4 mr-2 ${subscriptionStatus?.hasPaymentIssues && subscriptionStatus.status !== 'active' ? 'text-red-500' : ''}`} />
              {subscriptionStatus?.hasPaymentIssues && subscriptionStatus.status !== 'active' ? 'Fix Payment Issue' : 'Billing'}
            </DropdownMenuItem> */}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="hover:bg-gray-100" onClick={handleSignOut}>
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <AccountSheet 
        open={showSettings} 
        onOpenChange={setShowSettings}
      />
      <CrmIntegrationDialog 
        open={showIntegrations} 
        onOpenChange={setShowIntegrations} 
        userId={user?.id || ''}
      />
      <SubscriptionDialog
        open={showBilling}
        onOpenChange={setShowBilling}
        userId={user?.id || ''}
        showPlansInitially={true}
      />
      <TeamMembersDialog
        open={showTeamMembers}
        onOpenChange={setShowTeamMembers}
      />
    </>
  );
}
